<template>
  <v-card style="height: 100%">
    <v-text-field
      v-model="search"
      :placeholder="$t('Search')"
      append-icon="search"
      hide-details
      clearable
      class="mx-3 mb-2"
      style="font-size: 12px"
    />

    <v-treeview
      v-if="treeItems.length > 0"
      v-model="tree"
      :items="treeItems"
      item-key="code"
      item-text="name"
      item-children="validations"
      :selectable="selectable"
      open-all
      :active="active"
      :search="search"
      :filter="filter"
      class="scroll-fire-fox"
      style="max-height: 60vh; overflow: auto; text-align: left"
      ref="treeview"
    >
      <template v-slot:label="{ item }" style="overflow: hidden">
        <validation-label :item="item" />
      </template>

      <template v-slot:append="{ item, selected, indeterminate }">
        <validation-state
          v-if="item.state.running || item.state.result !== undefined"
          :item="item"
          :key="key === item.code ? key : undefined"
          @selectItem="selectValidationResult(item)"
        />
      </template>
    </v-treeview>

    <v-divider />

    <v-card-actions>
      <v-btn
        v-if="run.selection"
        block
        x-small
        color="accent"
        :disabled="runDisabled"
        @click="startRunningValidations"
      >
        {{ $t("Run") }}
      </v-btn>
      <v-btn
        v-if="run.complete"
        block
        x-small
        color="accent"
        @click="restartValidations"
      >
        {{ $t("Restart") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import HomeWindowButton from "../../../HomeWindowButton";
import ValidationState from "@/components/Home/Content/Tabs/Validations/ValidationSelection/ValidationState";
import ValidationLabel from "@/components/Home/Content/Tabs/Validations/ValidationSelection/ValidationLabel";

export default {
  name: "ValidationSelection",

  components: {
    ValidationLabel,
    ValidationState,
    HomeWindowButton
  },

  data() {
    return {
      originalListOfValidations: [],
      treeItems: [],
      tree: [],
      active: [],
      search: undefined,

      run: {
        selection: true,
        running: false,
        complete: false
      },

      key: undefined
    };
  },

  computed: {
    technicalLocalUUID() {
      return this.$store.getters.getNetworkDiagramTechnicalLocalUUID;
    },

    runDisabled() {
      return (
        !this.technicalLocalUUID ||
        this.treeItems.length === 0 ||
        this.tree.length === 0
      );
    },

    selectable() {
      return this.technicalLocalUUID && this.run.selection;
    },

    filter() {
      return (item, search, textKey) =>
        item.name.toLowerCase().includes(search.trim().toLowerCase()) ||
        item.description.toLowerCase().includes(search.trim().toLowerCase())
    },
  },

  watch: {
    "run.running"(isRunning) {
      this.$emit("updateRunState", isRunning);
    }
  },

  created() {
    let qualityControlResults = this.$store.getters.getQualityControl;
    if(qualityControlResults){
      this.run.complete=true;
      this.run.selection=false;
      this.treeItems = qualityControlResults;
    } else {
      this.getValidationsFromAPI();
    }
    
  },

  methods: {
    ...mapActions(["fetchValidations", "runNetworkValidation","SET_QUALITY_CONTROL_RESULTS",]),

    getValidationsFromAPI() {
      this.fetchValidations()
        .then(validations => {
          this.originalListOfValidations = validations;
          this.prepareTreeItems();
        })
        .catch(error => {
          console.error(error);
        });
    },

    prepareTreeItems() {
      let treeItems = Object.assign([], this.originalListOfValidations);
      treeItems = [
        {
          name: "All validations",
          description: "All validations",
          code: "all",
          validations: treeItems
        }
      ];
      this.addStateToValidations(treeItems);
      this.treeItems = treeItems;
    },

    addStateToValidations(validations) {
      validations.forEach(validation => {
        validation.state = {
          running: false,
          result: undefined
        };
        validation.results = undefined;
        if (validation.hasOwnProperty("validations")) {
          this.addStateToValidations(validation.validations);
        }
      });
    },

    startRunningValidations() {
      this.updateGeneralRunState("running");
      let allValidations = this.treeItems[0];
      let allPromises = [];
      allValidations.validations.forEach(validationGroup => {
        let groupPromises = [];
        validationGroup.validations.forEach(validation => {
          if (
            this.validationIsSelected(validation) &&
            !this.validationIsExcluded(validation)
          ) {
            validation.state.running = true;
            validationGroup.state.running = true;
            allValidations.state.running = true;
            groupPromises.push(this.runValidation(validation));
          }
        });
        if (groupPromises.length > 0) {
          allPromises.push(
            this.runValidationGroup(validationGroup, groupPromises)
          );
        }
      });
      this.runAllValidations(allValidations, allPromises);
      this.SET_QUALITY_CONTROL_RESULTS(this.treeItems);
    },

    validationIsSelected(validation) {
      let selectedValidations = this.tree;
      validation = selectedValidations.find(item => item === validation.code);
      return validation !== undefined;
    },

    validationIsExcluded(validation) {
      let excludedValidations = this.$refs.treeview.excludedItems;
      return excludedValidations.has(validation.code);
    },

    runValidation(validation) {
      return new Promise((resolve, reject) => {
        this.runNetworkValidation({
          endpoint: validation.code,
          technicalLocalUUID: this.technicalLocalUUID
        })
          .then(resp => {
            if (resp.status === 204 || resp.data.data.length === 0) {
              validation.state.result = "success";
            } else if (resp.data.data.length > 0) {
              validation.state.result = "warning";
              validation.results = resp.data.data;
            }
            resolve(validation.state.result);
          })
          .catch(() => {
            validation.state.result = "error";
            reject();
          })
          .then(() => {
            this.key = validation.code;
            validation.state.running = false;
          });
      });
    },

    runValidationGroup(validationGroup, promises) {
      return new Promise(resolve => {
        Promise.all(promises.map(this.reflect))
          .then(results => {
            let groupResult = "success";
            for (let validationResult of results) {
              if (
                validationResult.status === "rejected" ||
                validationResult.value === "warning"
              ) {
                groupResult = "warning";
              }
            }
            validationGroup.state.result = groupResult;
            resolve(groupResult);
          })
          .then(() => {
            this.key = validationGroup.code;
            validationGroup.state.running = false;
          });
      });
    },

    runAllValidations(allValidations, promises) {
      Promise.all(promises.map(this.reflect))
        .then(results => {
          let allResult = "success";
          for (let groupResult of results) {
            if (groupResult.value === "warning") {
              allResult = "warning";
            }
          }
          allValidations.state.result = allResult;
        })
        .then(() => {
          this.key = allValidations.code;
          allValidations.state.running = false;
          this.updateGeneralRunState("complete");
        });
    },

    reflect(promise) {
      return promise.then(
        v => {
          return { value: v, status: "fulfilled" };
        },
        e => {
          return { error: e, status: "rejected" };
        }
      );
    },

    selectValidationResult(item) {
      this.active = [item.code];
      this.$emit("selectResults", item.results);
    },

    cancelValidations() {
      this.updateGeneralRunState("selecting");
    },

    restartValidations() {
      this.updateGeneralRunState("selecting");
      this.resetValidationResults(this.treeItems);
      this.validationKey = 0;
      this.groupKey = 0;
      this.active = [];
      this.$emit("selectResults", undefined);
    },

    resetValidationResults(validations) {
      validations.forEach(validation => {
        validation.state = {
          result: undefined
        };
        validation.results = undefined;
        if (validation.hasOwnProperty("validations")) {
          this.addStateToValidations(validation.validations);
        }
      });
    },

    setValidationsAsRunning(validation, validationGroup) {
      let allValidations = this.treeItems[0];
      validation.state.running = true;
      validationGroup.state.running = true;
      allValidations.state.running = true;
    },

    updateGeneralRunState(state) {
      this.run.selection = state === "selecting";
      this.run.running = state === "running";
      this.run.complete = state === "complete";
    }
  }
};
</script>
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Validations/ValidationSelection/ValidationSelection.vue